/**
 * Entries Page - View and Manage Verified Entries
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Pagination,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { VerifiedEntry, EntrySearchParams } from '@/types/api';
import { EntryStatus } from '@/types/enums';
import { useEntries, useDeleteEntry } from '@/hooks/useEntries';
import { useLibraries } from '@/hooks/useLibraries';
import EntryList from '@/components/entries/EntryList';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorAlert from '@/components/common/ErrorAlert';
import { useTranslation } from 'react-i18next';

const ITEMS_PER_PAGE = 12;

const EntriesPage: React.FC = () => {
  const { t } = useTranslation('pages');
  // Filter state
  const [libraryId, setLibraryId] = useState<number | ''>('');
  const [status, setStatus] = useState<EntryStatus | ''>('');
  const [searchTitle, setSearchTitle] = useState('');
  const [searchAuthor, setSearchAuthor] = useState('');
  const [searchMsNo, setSearchMsNo] = useState('');
  const [page, setPage] = useState(1);

  // Build search params
  const searchParams: EntrySearchParams = {
    library_id: libraryId || undefined,
    status: status || undefined,
    title: searchTitle || undefined,
    author: searchAuthor || undefined,
    ms_no: searchMsNo || undefined,
    skip: (page - 1) * ITEMS_PER_PAGE,
    limit: ITEMS_PER_PAGE,
  };

  // Fetch data
  const { data: entriesArray = [], isLoading, isError, error } = useEntries(searchParams);
  const { data: libraries } = useLibraries();
  const deleteEntry = useDeleteEntry();

  // Handle pagination for array response
  const entries = entriesArray;
  const totalEntries = entriesArray.length;
  const totalPages = Math.ceil(totalEntries / ITEMS_PER_PAGE);

  const handleDelete = async (entry: VerifiedEntry) => {
    if (
      !window.confirm(
        t('entries.errors.confirmDelete', { title: entry.title })
      )
    ) {
      return;
    }

    try {
      await deleteEntry.mutateAsync(entry.id);
    } catch (error) {
      console.error('Failed to delete entry:', error);
    }
  };

  const handleClearFilters = () => {
    setLibraryId('');
    setStatus('');
    setSearchTitle('');
    setSearchAuthor('');
    setSearchMsNo('');
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasFilters = libraryId || status || searchTitle || searchAuthor || searchMsNo;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('entries.title')}
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <FilterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t('entries.filters.title')}
          </Typography>
          {hasFilters && (
            <Button size="small" onClick={handleClearFilters}>
              {t('entries.filters.clearButton')}
            </Button>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('entries.filters.libraryLabel')}</InputLabel>
              <Select
                value={libraryId}
                onChange={(e) => {
                  setLibraryId(e.target.value as number | '');
                  setPage(1);
                }}
                label={t('entries.filters.libraryLabel')}
              >
                <MenuItem value="">{t('entries.filters.allLibraries')}</MenuItem>
                {libraries?.map((library) => (
                  <MenuItem key={library.id} value={library.id}>
                    {library.canonical_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('entries.filters.statusLabel')}</InputLabel>
              <Select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as EntryStatus | '');
                  setPage(1);
                }}
                label={t('entries.filters.statusLabel')}
              >
                <MenuItem value="">{t('entries.filters.allStatuses')}</MenuItem>
                <MenuItem value={EntryStatus.PUBLISHED}>{t('entries.filters.published')}</MenuItem>
                <MenuItem value={EntryStatus.REVIEWED}>{t('entries.filters.reviewed')}</MenuItem>
                <MenuItem value={EntryStatus.DRAFT}>{t('entries.filters.draft')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} lg={2}>
            <TextField
              label={t('entries.filters.searchTitle')}
              size="small"
              fullWidth
              value={searchTitle}
              onChange={(e) => {
                setSearchTitle(e.target.value);
                setPage(1);
              }}
              placeholder={t('entries.filters.searchTitlePlaceholder')}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={2}>
            <TextField
              label={t('entries.filters.searchAuthor')}
              size="small"
              fullWidth
              value={searchAuthor}
              onChange={(e) => {
                setSearchAuthor(e.target.value);
                setPage(1);
              }}
              placeholder={t('entries.filters.searchAuthorPlaceholder')}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={2}>
            <TextField
              label={t('entries.filters.searchMsNo')}
              size="small"
              fullWidth
              value={searchMsNo}
              onChange={(e) => {
                setSearchMsNo(e.target.value);
                setPage(1);
              }}
              placeholder={t('entries.filters.searchMsNoPlaceholder')}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
        </Grid>

        {/* Results count */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {isLoading ? t('entries.resultsCount.loading') : t('entries.resultsCount.found', { count: totalEntries })}
        </Typography>
      </Paper>

      {/* Loading State */}
      {isLoading && <LoadingSpinner message={t('entries.loading')} />}

      {/* Error State */}
      {isError && <ErrorAlert error={error} title={t('entries.errors.failedToLoad')} />}

      {/* Delete Error */}
      {deleteEntry.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => deleteEntry.reset()}>
          {t('entries.errors.failedToDelete')}
        </Alert>
      )}

      {/* Entries List */}
      {!isLoading && <EntryList entries={entries} onDelete={handleDelete} />}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

export default EntriesPage;
